import axios from 'axios';
import { CompanyInfo, VerificationResult } from '../types';
import { getVatError, getGeneralError, SupportedLanguage } from '../utils/errorTranslations';
import { Analytics, ErrorTracker, PerformanceTracker } from '../utils/analytics';

// EU VAT validation service using VIES (VAT Information Exchange System)
export class VatVerificationService {
  private static readonly VIES_API_URL = '/api/vies/';
  private static useRealAPI = true; // Toggle for real vs simulated API calls

  static setUseRealAPI(useReal: boolean) {
    this.useRealAPI = useReal;
  }

  static async verifyVatNumber(companyInfo: CompanyInfo, language: SupportedLanguage = 'en'): Promise<VerificationResult> {
    const identifier = companyInfo.vatNumber;
    
    try {
      Analytics.verificationStarted('vat', 'single');
      PerformanceTracker.startTimer(`vat_verification_${identifier}`);
      
      const vatNumber = companyInfo.vatNumber.replace(/\s/g, '');
      const countryCode = vatNumber.substring(0, 2);
      const vatId = vatNumber.substring(2);

      // Choose between real API call or simulation
      const result = this.useRealAPI 
        ? await this.callViesAPI(countryCode, vatId, companyInfo, language)
        : await this.simulateViesCall(countryCode, vatId, companyInfo, language);

      const responseTime = PerformanceTracker.endTimer(`vat_verification_${identifier}`, 'api_response_time', { service: 'vat' });
      console.log('Response time:', responseTime);
      if (result.isValid) {
        Analytics.verificationSuccess('vat', 'single');
      } else {
        Analytics.verificationError('vat', 'single', result.error || 'validation_failed');
      }

      return {
        vatNumber: companyInfo.vatNumber,
        isValid: result.isValid,
        companyName: result.companyName,
        address: result.address,
        country: result.country,
        timestamp: new Date().toISOString(),
        error: result.error
      };
    } catch (error) {
      const responseTime = PerformanceTracker.endTimer(`vat_verification_${identifier}`);
      console.log('Response time:', responseTime);
      Analytics.verificationError('vat', 'single', 'api_error');
      ErrorTracker.trackApiError('vat', error, 'verifyVatNumber');
      
      return {
        vatNumber: companyInfo.vatNumber,
        isValid: false,
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : getGeneralError('unknownError', language)
      };
    }
  }

  private static async callViesAPI(
    countryCode: string, 
    vatId: string, 
    companyInfo: CompanyInfo,
    language: SupportedLanguage = 'en'
  ): Promise<{
    isValid: boolean;
    companyName?: string;
    address?: string;
    country?: string;
    error?: string;
  }> {
    const maxRetries = 7;
    const retryDelay = 2000; // 2 seconds
    
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // VIES API endpoint for checking VAT numbers
        const response = await axios.get(`${this.VIES_API_URL}${countryCode}/vat/${vatId}`);
        
        if (response.data && response.data.isValid !== undefined) {
          return {
            isValid: response.data.isValid,
            companyName: response.data.name || 'unknown',
            address: response.data.address || 'unknown',
            country: this.getCountryName(countryCode) || countryCode
          };
        } else {
          throw new Error('No match found in response - retrying');
        }
      } catch (error) {
        console.error(`VIES API Error (attempt ${attempt}/${maxRetries}):`, error);
        
        // If this is the last attempt, return the error
        if (attempt === maxRetries) {
          return {
            isValid: false,
            error: getVatError('serviceUnavailable', language)
          };
        }
        
        // Wait before retrying (except on the last attempt)
        console.log(`Retrying in ${attempt * retryDelay}ms... (attempt ${attempt + 1}/${maxRetries})`);
        await new Promise(resolve => setTimeout(resolve, attempt * retryDelay));
      }
    }
    
    // This should never be reached, but just in case
    return {
      isValid: false,
      error: getVatError('serviceUnavailable', language)
    };
  }

  private static async simulateViesCall(
    countryCode: string, 
    vatId: string, 
    companyInfo: CompanyInfo,
    language: SupportedLanguage = 'en'
  ): Promise<{
    isValid: boolean;
    companyName?: string;
    address?: string;
    country?: string;
    error?: string;
  }> {
    // Simulate API delay
    await new Promise(resolve => setTimeout(resolve, 1000 + Math.random() * 2000));

    // Simulate different responses based on VAT number patterns
    const isValid = this.validateVatNumberPattern(countryCode, vatId);
    
    if (!isValid) {
      return {
        isValid: false,
        error: getVatError('invalidFormat', language)
      };
    }

    // Simulate successful verification
    return {
      isValid: true,
      companyName: companyInfo.name || `Company ${vatId}`,
      address: companyInfo.address || `${countryCode} Address`,
      country: this.getCountryName(countryCode)
    };
  }

  private static validateVatNumberPattern(countryCode: string, vatId: string): boolean {
    // Basic validation patterns for different EU countries
    const patterns: { [key: string]: RegExp } = {
      'AT': /^U\d{8}$/,
      'BE': /^\d{10}$/,
      'BG': /^\d{9,10}$/,
      'CY': /^\d{8}[A-Z]$/,
      'CZ': /^\d{8,10}$/,
      'DE': /^\d{9}$/,
      'DK': /^\d{8}$/,
      'EE': /^\d{9}$/,
      'EL': /^\d{9}$/,
      'ES': /^[A-Z]\d{8}$/,
      'FI': /^\d{8}$/,
      'FR': /^[A-Z]{2}\d{9}$/,
      'HR': /^\d{11}$/,
      'HU': /^\d{8}$/,
      'IE': /^\d[A-Z0-9+*]\d{5}[A-Z]$/,
      'IT': /^\d{11}$/,
      'LT': /^(\d{9}|\d{12})$/,
      'LU': /^\d{8}$/,
      'LV': /^\d{11}$/,
      'MT': /^\d{8}$/,
      'NL': /^\d{9}B\d{2}$/,
      'PL': /^\d{10}$/,
      'PT': /^\d{9}$/,
      'RO': /^\d{2,10}$/,
      'SE': /^\d{12}$/,
      'SI': /^\d{8}$/,
      'SK': /^\d{10}$/
    };

    const pattern = patterns[countryCode];
    return pattern ? pattern.test(vatId) : false;
  }

  private static getCountryName(countryCode: string): string {
    const countries: { [key: string]: string } = {
      'AT': 'Austria',
      'BE': 'Belgium',
      'BG': 'Bulgaria',
      'CY': 'Cyprus',
      'CZ': 'Czech Republic',
      'DE': 'Germany',
      'DK': 'Denmark',
      'EE': 'Estonia',
      'EL': 'Greece',
      'ES': 'Spain',
      'FI': 'Finland',
      'FR': 'France',
      'HR': 'Croatia',
      'HU': 'Hungary',
      'IE': 'Ireland',
      'IT': 'Italy',
      'LT': 'Lithuania',
      'LU': 'Luxembourg',
      'LV': 'Latvia',
      'MT': 'Malta',
      'NL': 'Netherlands',
      'PL': 'Poland',
      'PT': 'Portugal',
      'RO': 'Romania',
      'SE': 'Sweden',
      'SI': 'Slovenia',
      'SK': 'Slovakia'
    };

    return countries[countryCode] || countryCode;
  }

  static async verifyBatch(companies: CompanyInfo[]): Promise<VerificationResult[]> {
    const results: VerificationResult[] = [];
    
    for (const company of companies) {
      const result = await this.verifyVatNumber(company);
      results.push(result);
    }
    
    return results;
  }
}
